import { useEffect, useMemo, useRef, useState } from "react";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  paramOr,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  listPosTerminals,
  listPosTickets,
  type PosTicketListSort,
} from "@/shared/api/pos";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { POS_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { posModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { posTicketListColumns } from "./pos-tickets-columns";

type LifecycleFilter = "all" | "draft" | "parked" | "posted" | "void";

const PAGE_SIZE = 50;
const LIFECYCLES = ["all", "draft", "parked", "posted", "void"] as const;
const SORTABLE: PosTicketListSort[] = [
  "number",
  "lifecycle",
  "grossTotal",
  "terminalName",
  "createdAt",
];

function sortingToApi(sorting: SortingState): {
  sort: PosTicketListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as PosTicketListSort)) {
    return {
      sort: first.id as PosTicketListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function PosTicketsPage() {
  const { companySlug } = useParams();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "all",
  ) as LifecycleFilter;
  const setLifecycleFilter = (next: LifecycleFilter) =>
    patchParams((p) => setOrDelete(p, "lifecycle", next));
  const terminalId = searchParams.get("terminal") ?? "";
  const setTerminalId = (next: string) =>
    patchParams((p) => setOrDelete(p, "terminal", next));
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);
  const listPath = companySlug
    ? `/c/${companySlug}/pos/tickets`
    : "/pos/tickets";
  const listReturnHandled = useRef(false);
  const { flashId, flashRow } = useListRowFlash();

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const serverLifecycle =
    lifecycleFilter === "all" ? undefined : lifecycleFilter;

  const terminalsQuery = useQuery({
    queryKey: queryKeys.pos.terminals(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listPosTerminals(companySlug!),
  });

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.pos.tickets(companySlug ?? "", {
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      terminalId: terminalId || undefined,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listPosTickets(companySlug!, {
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        lifecycle: serverLifecycle,
        terminalId: terminalId || undefined,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const errorMsg = queryErrorMessage(
    listQuery.error ?? terminalsQuery.error,
    listQuery.isError || terminalsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const columns = useMemo(
    () =>
      companySlug
        ? posTicketListColumns(companySlug, {
            listPath,
            terminalId,
            setTerminalId,
          })
        : [],
    // setTerminalId is stable via patchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companySlug, listPath, terminalId],
  );

  const tickets = listQuery.data?.pages.flatMap((p) => p.tickets) ?? [];
  const total = listQuery.data?.pages[0]?.total;
  const terminals = terminalsQuery.data?.terminals ?? [];
  const loading = listQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const filtersActive =
    Boolean(query.trim()) ||
    lifecycleFilter !== "all" ||
    Boolean(terminalId);
  const terminalLabel =
    terminals.find((t) => t.id === terminalId)?.name ?? terminalId;
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: listQuery.isFetching,
  });

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(listPath);
    if (id) flashRow(id);
  }, [companySlug, loading, listPath, flashRow]);

  if (!companySlug) return null;

  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="POS sales"
        description="Tickets from the register — separate from sales orders"
        tabs={
          <DeskSubNav
            placement="header"
            items={posModuleTabs(companySlug)}
            aria-label="POS views"
          />
        }
      />

      <AdminFilters>
        <input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search ticket number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading && tickets.length === 0}
        />
        <select
          className={adminFilterControlClass}
          value={lifecycleFilter}
          onChange={(e) =>
            setLifecycleFilter(e.target.value as LifecycleFilter)
          }
        >
          <option value="all">All lifecycle</option>
          <option value="posted">Posted</option>
          <option value="parked">Parked</option>
          <option value="draft">Draft</option>
          <option value="void">Void</option>
        </select>
        <select
          className={adminFilterControlClass}
          value={terminalId}
          onChange={(e) => setTerminalId(e.target.value)}
        >
          <option value="">All terminals</option>
          {terminals.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <SavedListViewsControl
          listId="pos-tickets"
          companySlug={companySlug}
          currentQs={searchParams.toString()}
          onApplyQs={(qs) => {
            replaceAllParams(qs);
            const q = readQ(new URLSearchParams(qs));
            setQuery(q);
            setDebouncedQ(q);
          }}
        />
        <ActiveFilterChips
          chips={[
            ...(lifecycleFilter !== "all"
              ? [
                  {
                    key: "lifecycle",
                    label:
                      lifecycleFilter[0]!.toUpperCase() +
                      lifecycleFilter.slice(1),
                    onClear: () => setLifecycleFilter("all"),
                  },
                ]
              : []),
            ...(terminalId
              ? [
                  {
                    key: "terminal",
                    label: `Terminal: ${terminalLabel}`,
                    onClear: () => setTerminalId(""),
                  },
                ]
              : []),
          ]}
        />
      </AdminFilters>

      <DocListLegend kinds={POS_LIFECYCLE_LEGEND} />
      <ListSearchStatus online searching={searchingOnline} />

      {!loading && tickets.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {tickets.length}
          {total > tickets.length ? ` of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && tickets.length === 0 && (
        <Alert variant="muted" className="mt-8">
          Loading POS tickets…
        </Alert>
      )}

      {!loading && tickets.length === 0 && !error ? (
        <div className="mt-8">
          <EmptyState
            title={filtersActive ? "No tickets match" : "No POS tickets yet"}
            description={
              filtersActive
                ? "Try clearing search, lifecycle, or terminal."
                : "Ring a sale on the Floor to create tickets."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link
                    to={`/c/${companySlug}/pos`}
                    className="no-underline"
                  >
                    Terminals
                  </Link>
                </Button>
              )
            }
          />
        </div>
      ) : null}

      {tickets.length > 0 ? (
        <>
          <DataTable
            listId="pos-tickets"
            companySlug={companySlug}
            columns={columns}
            data={tickets}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            highlightedRowId={flashId}
            onRefresh={() => void listQuery.refetch()}
          />
          {listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={listQuery.isFetchingNextPage}
                pending={listQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void listQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
          </DataTableChrome>
    </PageFrame>
  );
}
